import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { FormButtons } from "../../components";
import { useStore } from "../../helpers";
import { safe_get } from "../../report-visuals/report-utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";

import { RNSelect } from "../../components/form-components";
import { DownloadAsExcelSheet } from "../../components/DownloadAsExcelSheet";
import { userRoles } from "./helpers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VortexSpinner } from "../digital-repository";
import { defaultUser, userValidationSchema } from "./add-user";

const PERMISSION_MAP: Record<string, string> = {
    "View voter records": "voter_records_access",
    "Edit voter records": "voter_records_edit",
    "View updated voter records": "voter_records_upsync",
    "Get out the vote": "gotv_overall",
    "Get out the vote (Booth level)": "gotv_booth",
    "Voter Actions": "voter_actions",
    "Manage all users": "user_manage_all",
    "Manage users assigned": "user_manage_team",
    "Deactivate users": "user_archive",
    "View Analytical Summary": "view_analytical_summary",
    "Edit Tasks": "edit_tasks",
    "View Tasks": "view_tasks",
    "Manage Beneficiaries": "manage_fs_beneficiary",
    "Manage Project Works": "manage_fs_projectworks",
    "Manage Grievances": "manage_fs_grievance",
};

export const GRID_COLUMNS = [
    { label: "Name", key: "Name" },
    { label: "Phone Number", key: "Phone Number" },
    { label: "Email", key: "Email" },
];

export const DEFAULT_GRID_DATA = [{ Name: "", Email: "", "Phone Number": "" }];

const phoneRegex = /^[6-9]\d{9}$/;

type BulkFormType = {
    role: string;
    permissions: Record<string, boolean>;
};

type BulkUserRow = {
    Name?: string;
    Email?: string;
    "Phone Number"?: string;
    isInvalid?: boolean;
    [k: string]: any;
};

export const AddBulkUsers = observer(
    ({ onClose }: { onClose?: () => void }) => {
        const rootStore = useStore();
        const { userStore } = rootStore;
        const { t } = useTranslation(["user_management"]);
        const { projectid } = useParams();
        const roleFromStore = safe_get(userStore.user, "role", "");
        const [uploadedFile, setUploadedFile] = useState<File | null>(null);
        const [previewRows, setPreviewRows] =
            useState<BulkUserRow[]>(DEFAULT_GRID_DATA);
        const [uploadReport, setUploadReport] = useState<any[]>([]);
        const queryClient = useQueryClient();

        const methods = useForm<any>({
            defaultValues: {
                ...defaultUser,
            },
            resolver: yupResolver(userValidationSchema),
        });
        //
        // const user_roles = userRoles.filter((r) => {
        //   if (roleFromStore === "client" && r.value === "root") return;
        //   return r;
        // });
        const user_roles = userRoles.filter((r) => {
            // Only root can see superadmin
            if (r.value === "superadmin" && roleFromStore !== "root")
                return false;

            // Only root can see root role
            if (r.value === "root" && roleFromStore !== "root") return false;

            return true;
        });

        // const getVisibleUserRoles = (currentUserRole: string) => {
        //     return userRoles.filter((r) => {
        //         // Only root can see superadmin
        //         if (r.value === "superadmin" && currentUserRole !== "root")
        //             return false;
        //
        //         // Only root can see root
        //         if (r.value === "root" && currentUserRole !== "root")
        //             return false;
        //
        //         return true;
        //     });
        // };

        const handleFileUpload = (file: File) => {
            setUploadedFile(file);
            const reader = new FileReader();

            reader.onload = (e) => {
                const content = e.target?.result as string;
                const cleanContent = content.replace(/\r/g, "").trim();
                const delimiter = cleanContent.includes("\t") ? "\t" : ",";

                const lines = cleanContent
                    .split("\n")
                    .filter((line) => line.trim() !== "");
                if (lines.length < 2) {
                    toast.error(
                        "Uploaded file seems empty or has no data rows"
                    );
                    return;
                }

                const headers = lines[0].split(delimiter).map((h) => h.trim());
                const nameIndex = headers.findIndex((h) => /name/i.test(h));
                const emailIndex = headers.findIndex((h) => /email/i.test(h));
                const phoneIndex = headers.findIndex((h) =>
                    /(phone|number)/i.test(h)
                );

                if (
                    nameIndex === -1 ||
                    emailIndex === -1 ||
                    phoneIndex === -1
                ) {
                    toast.error(
                        "Missing required columns: Name, Email, Phone Number"
                    );
                    return;
                }

                const newRows = lines.slice(1).map((line) => {
                    const cols = line.split(delimiter).map((c) => c.trim());
                    const raw = cols[phoneIndex] || "";
                    const cleaned = raw.replace(/\D/g, "").slice(0, 10);
                    return {
                        Name: cols[nameIndex] || "",
                        Email: cols[emailIndex] || "",
                        "Phone Number": cleaned,
                        isInvalid: cleaned.length !== 10,
                    };
                });

                setPreviewRows(newRows.length ? newRows : DEFAULT_GRID_DATA);
                toast.success("File Uploaded");
            };

            reader.readAsText(file);
        };

        const handleCellChange = (
            index: number,
            key: keyof BulkUserRow,
            value: string
        ) => {
            setPreviewRows((prev) => {
                const updated = [...prev];
                if (key === "Phone Number") {
                    const digits = (value || "")
                        .replace(/\D/g, "")
                        .slice(0, 10);
                    updated[index] = { ...updated[index], [key]: digits };
                    updated[index].isInvalid = digits.length !== 10;
                } else {
                    updated[index] = { ...updated[index], [key]: value };
                }
                return updated;
            });
        };

        const handleAddRow = () => {
            setPreviewRows((prev) => [...prev, { ...DEFAULT_GRID_DATA[0] }]);
        };

        const handleDeleteRow = (index: number) => {
            setPreviewRows((prev) => prev.filter((_, i) => i !== index));
        };

        const { mutate, isLoading } = useMutation({
            mutationFn: async (payload: any) => userStore.addBulkUser(payload),
            onSuccess: (res: any) => {
                const reportData =
                    res?.report ||
                    res?.data?.report ||
                    res?.result?.report ||
                    res?.response?.report ||
                    [];

                if (reportData.length > 0) {
                    setUploadReport(reportData);
                    const allSuccess = reportData.every(
                        (item: any) => item.response === true
                    );
                    if (allSuccess) {
                        toast.success("All users added successfully!");
                        setTimeout(() => {
                            if (onClose) onClose();
                            else userStore.set_add_bulk_user(false);
                            queryClient.invalidateQueries(["users", projectid]);
                        }, 1500);
                    } else {
                        toast.info(
                            "Some users failed to upload. Please review the report."
                        );
                    }
                } else if (res?.success || res?.status === 200) {
                    toast.success("Users added successfully!");
                    setTimeout(() => {
                        if (onClose) onClose();
                        else userStore.set_add_bulk_user(false);
                    }, 1000);
                } else {
                    toast.error("Something went wrong while adding users!");
                }
            },
            onError: () => toast.error("Bulk user upload failed!"),
        });

        if (isLoading) return <VortexSpinner visible={true} />;

        const onSubmit = (data: BulkFormType) => {
            const validUsers = previewRows.filter(
                (u) => u.Name || u.Email || u["Phone Number"]
            );
            if (validUsers.length === 0) {
                toast.error("No user data found in table");
                return;
            }
            const updatedRows = validUsers.map((u) => {
                const cleanedPhone = (u["Phone Number"] || "").replace(
                    /\D/g,
                    ""
                );
                return {
                    ...u,
                    isInvalid: !phoneRegex.test(cleanedPhone),
                };
            });

            setPreviewRows(updatedRows);

            const invalidRows = updatedRows.filter((r) => r.isInvalid);
            if (invalidRows.length > 0) {
                toast.info(
                    `${invalidRows.length} phone number(s) are invalid and will not be saved.`
                );
            }

            const users = updatedRows.map((u) => {
                const cleaned = (u["Phone Number"] || "").replace(/\D/g, "");
                const phoneValue = phoneRegex.test(cleaned)
                    ? cleaned
                    : undefined;
                return {
                    name: u.Name?.trim(),
                    email: u.Email?.trim(),
                    ...(phoneValue ? { phone: phoneValue } : {}),
                };
            });

            const formattedPermissions: Record<string, boolean> = {};
            Object.entries(data.permissions).forEach(([label, value]) => {
                const backendKey = PERMISSION_MAP[label];
                if (backendKey) formattedPermissions[backendKey] = value;
            });

            const payload = {
                projectid: parseInt(projectid || "0"),
                role: data.role,
                permissions: formattedPermissions,
                users,
            };
            mutate(payload);
        };

        const permissionsList = Object.keys(PERMISSION_MAP);

        return (
            <div>
                <FormProvider {...methods}>
                    <form
                        className="flex flex-col gap-y-3"
                        onSubmit={methods.handleSubmit(onSubmit)}
                    >
                        <RNSelect
                            name={"role"}
                            label={"Role"}
                            // options={user_roles}
                            options={userStore.visibleUserRoles}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            menuPlacement={"auto"}
                            styles={{
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                            }}
                        />

                        <div className="bg-purple-50 border border-purple-200 text-purple-700 rounded-lg p-3 text-sm leading-relaxed">
                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>
                                    Click <b>Download Template</b> to get a CSV
                                    with columns:
                                    <span className="text-purple-600 font-medium">
                                        {" "}
                                        Name, Email, Phone Number
                                    </span>
                                    .
                                </li>
                                <li>Fill in the file (one user per row).</li>
                                <li>
                                    Or directly edit the grid below before
                                    adding.
                                </li>
                            </ul>
                        </div>

                        <div className="flex flex-row justify-between ">
                            <DownloadAsExcelSheet
                                data={DEFAULT_GRID_DATA}
                                cols={GRID_COLUMNS}
                                name="User Upload Template"
                                classes="w-full"
                            />

                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) =>
                                    e.target.files?.[0] &&
                                    handleFileUpload(e.target.files[0])
                                }
                                className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer focus:outline-none p-2"
                            />
                        </div>

                        <div className="mt-2 border rounded-md p-2 bg-gray-50 text-sm">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-medium text-gray-800">
                                    User Details:
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddRow}
                                    className="bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-md hover:bg-purple-200"
                                >
                                    + Add User Details
                                </button>
                            </div>

                            <div className="overflow-x-auto rounded-md">
                                <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                    <thead>
                                        <tr className="bg-purple-100 text-purple-800">
                                            {GRID_COLUMNS.map((col) => (
                                                <th
                                                    key={col.key}
                                                    className="px-4 py-2 border border-gray-200 text-left font-semibold"
                                                >
                                                    {col.label}
                                                </th>
                                            ))}
                                            <th className="px-4 py-2 border border-gray-200 text-center font-semibold">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-purple-50 transition-colors`}
                                            >
                                                {GRID_COLUMNS.map((col) => (
                                                    <td
                                                        key={col.key}
                                                        className="px-4 py-2 border border-gray-200"
                                                    >
                                                        <input
                                                            type={
                                                                col.key ===
                                                                "Email"
                                                                    ? "email"
                                                                    : "text"
                                                            }
                                                            inputMode={
                                                                col.key ===
                                                                "Phone Number"
                                                                    ? "numeric"
                                                                    : undefined
                                                            }
                                                            pattern={
                                                                col.key ===
                                                                "Phone Number"
                                                                    ? "[0-9]*"
                                                                    : undefined
                                                            }
                                                            value={
                                                                row[col.key] ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(
                                                                    idx,
                                                                    col.key,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className={`w-full border rounded-md p-1 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none ${
                                                                col.key ===
                                                                    "Phone Number" &&
                                                                row.isInvalid
                                                                    ? "border-red-500 bg-red-50"
                                                                    : "border-gray-300"
                                                            }`}
                                                            placeholder={`Enter ${col.label}`}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-4 py-2 border border-gray-200 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleDeleteRow(idx)
                                                        }
                                                        className="text-red-600 hover:text-red-800 text-sm"
                                                    >
                                                        ✖
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg bg-white shadow-sm mt-3 p-4">
                            <h3 className="text-purple-600 font-semibold mb-3">
                                Permissions
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                {permissionsList.map((perm, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0"
                                    >
                                        <label
                                            htmlFor={`permission-${idx}`}
                                            className="text-gray-800 text-sm font-medium cursor-pointer"
                                        >
                                            {perm}
                                        </label>
                                        <Controller
                                            name={`permissions.${perm}`}
                                            control={methods.control}
                                            defaultValue={false}
                                            render={({ field }) => (
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        id={`permission-${idx}`}
                                                        type="checkbox"
                                                        checked={field.value}
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                e.target.checked
                                                            )
                                                        }
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-purple-500 transition-colors duration-300"></div>
                                                    <span className="absolute left-1 top-[2px] bg-white w-5 h-5 rounded-full transition-transform duration-300 peer-checked:translate-x-5"></span>
                                                </label>
                                            )}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <FormButtons
                            onSave={() => {}}
                            onCancel={() => {
                                methods.reset();
                                setPreviewRows(DEFAULT_GRID_DATA);
                                setUploadedFile(null);
                                setUploadReport([]);
                                if (onClose) onClose();
                                else userStore.set_add_bulk_user(false);
                            }}
                            type={"submit"}
                        />
                    </form>
                </FormProvider>
            </div>
        );
    }
);
